import { logger } from "@/lib/logger";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAutoBillingTrigger } from "./useAutoBillingTrigger";

interface PatientVisit {
  id: string;
  patient_id: string;
  trial_site_id: string;
  visit_type: string;
  visit_number: number;
  scheduled_date: string;
  actual_date: string | null;
  visit_status: 'scheduled' | 'completed' | 'missed' | 'rescheduled' | 'cancelled';
  protocol_deviation: boolean;
  deviation_notes: string | null;
  conducted_by: string | null;
  created_at: string;
  updated_at: string;
  patient?: {
    first_name: string;
    last_name: string;
    patient_number: string;
  };
}

interface UpdateVisitParams {
  visitId: string;
  status: 'completed' | 'missed' | 'rescheduled' | 'cancelled';
  actualDate?: string;
  notes?: string;
  protocolDeviation?: boolean;
}

// usePatientVisits is consolidated in src/hooks/usePatientVisits.ts
// Re-export here for backward compatibility with existing imports
export { usePatientVisits } from './usePatientVisits';

export function useTrialSiteVisits(trialSiteId?: string, status?: string) {
  return useQuery({
    queryKey: ['trial-site-visits', trialSiteId, status],
    queryFn: async () => {
      if (!trialSiteId) return [];

      let query = supabase
        .from('patient_visits')
        .select(`
          *,
          profiles!patient_visits_patient_id_fkey(first_name, last_name, patient_number)
        `)
        .eq('trial_site_id', trialSiteId)
        .order('scheduled_date', { ascending: true });

      if (status) {
        query = query.eq('visit_status', status as any);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      // Transform to expected shape
      return (data || []).map((item: any) => ({
        ...item,
        patient: item.profiles,
      })) as PatientVisit[];
    },
    enabled: !!trialSiteId,
  });
}

export function useUpdateVisitStatus() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { triggerAutoBilling } = useAutoBillingTrigger();

  return useMutation({
    mutationFn: async ({ visitId, status, actualDate, notes, protocolDeviation }: UpdateVisitParams) => {
      // First get the visit details for billing
      const { data: visit, error: fetchError } = await supabase
        .from('patient_visits')
        .select('*, trial_sites!inner(organization_id)')
        .eq('id', visitId)
        .single();

      if (fetchError) throw fetchError;

      // Update visit status
      const updates: any = {
        visit_status: status,
        updated_at: new Date().toISOString(),
      };

      if (actualDate) {
        updates.actual_date = actualDate;
      }

      if (status === 'completed' && !actualDate) {
        updates.actual_date = new Date().toISOString().split('T')[0];
      }

      if (notes) {
        updates.deviation_notes = notes;
      }

      if (protocolDeviation !== undefined) {
        updates.protocol_deviation = protocolDeviation;
      }

      // Get current user for conducted_by
      const { data: user } = await supabase.auth.getUser();
      if (user?.user?.id && status === 'completed') {
        updates.conducted_by = user.user.id;
      }

      const { data, error } = await supabase
        .from('patient_visits')
        .update(updates)
        .eq('id', visitId)
        .select()
        .single();

      if (error) throw error;

      return { visit: data, originalVisit: visit };
    },
    onSuccess: async ({ visit, originalVisit }) => {
      // Log to audit trail
      try {
        const { data: currentUser } = await supabase.auth.getUser();
        if (currentUser?.user?.id) {
          await supabase.from('audit_trails').insert({
            user_id: currentUser.user.id,
            action: `visit_${visit.visit_status}`,
            entity_type: 'patient_visit',
            entity_id: visit.id,
            details: {
              visit_type: visit.visit_type,
              visit_number: visit.visit_number,
              scheduled_date: visit.scheduled_date,
              actual_date: visit.actual_date,
              status: visit.visit_status,
              patient_id: visit.patient_id,
              trial_site_id: visit.trial_site_id,
              notes: visit.deviation_notes,
            },
            previous_values: {
              visit_status: originalVisit?.visit_status,
            },
            risk_level: visit.visit_status === 'cancelled' ? 'medium' : 'low',
          });
        }
      } catch (auditError) {
        logger.error('Failed to log visit audit trail:', auditError);
      }

      // Trigger auto-billing based on visit status
      const eventTriggerMap: Record<string, string> = {
        'completed': `${visit.visit_type}_visit`,
        'missed': 'visit_missed',
      };

      const eventTrigger = eventTriggerMap[visit.visit_status];
      
      if (eventTrigger) {
        // Get trial ID from trial_sites
        const { data: trialSiteData } = await supabase
          .from('cro_trial_sites')
          .select('cro_trial_id, cro_trials!inner(trial_id)')
          .eq('site_organization_id', originalVisit.trial_sites?.organization_id)
          .single();

        const trialId = (trialSiteData?.cro_trials as any)?.trial_id;

        if (trialId) {
          await triggerAutoBilling({
            trial_id: trialId,
            trial_site_id: visit.trial_site_id,
            patient_id: visit.patient_id,
            event_trigger_type: eventTrigger,
            visit_id: visit.id,
            notes: `Visit ${visit.visit_type} ${visit.visit_status}`,
          });
        }
      }

      // Queue missed visit follow-up notification to patient
      if (visit.visit_status === 'missed') {
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('user_id, first_name, last_name, whatsapp_opted_in')
            .eq('id', visit.patient_id)
            .single();

          const { data: site } = await supabase
            .from('trial_sites_safe')
            .select('name')
            .eq('id', visit.trial_site_id)
            .single();

          if (profile && site) {
            const visitDate = new Date(visit.scheduled_date);
            const formattedDate = visitDate.toLocaleDateString('en-US', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
            });
            const visitLabel = visit.visit_type.replace(/_/g, ' ');

            await supabase
              .from('notification_queue')
              .insert([{
                user_id: profile.user_id,
                template_id: null,
                channel: profile.whatsapp_opted_in ? 'whatsapp' : 'sms',
                priority: 2,
                variables: {
                  patient_name: `${profile.first_name} ${profile.last_name}`,
                  visit_type: visitLabel.toUpperCase(),
                  visit_date: formattedDate,
                  site_name: site.name,
                  fallback_content: `Dear ${profile.first_name}, you missed your ${visitLabel} visit scheduled for ${formattedDate} at ${site.name}. Please contact us to reschedule at your earliest convenience.`
                },
                metadata: {
                  visit_id: visit.id,
                  trial_site_id: visit.trial_site_id,
                  notification_type: 'visit_missed_followup'
                },
                scheduled_for: new Date().toISOString(),
                status: 'pending'
              }]);
          }
        } catch (notifError) {
          logger.error('Failed to queue missed visit notification:', notifError);
        }
      }

      toast({
        title: "Visit updated",
        description: `Visit marked as ${visit.visit_status}`,
      });

      queryClient.invalidateQueries({ queryKey: ['patient-visits'] });
      queryClient.invalidateQueries({ queryKey: ['patient-visits-trial'] });
      queryClient.invalidateQueries({ queryKey: ['trial-site-visits'] });
      queryClient.invalidateQueries({ queryKey: ['site-visits-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['billable-events'] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update visit",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });
}
